import { Component, Input, OnDestroy, OnInit, signal, ChangeDetectionStrategy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';
import { FormsModule } from '@angular/forms';
import {
  ActivatedRoute,
  NavigationEnd,
  Router,
  NavigationExtras,
} from '@angular/router';
import { ModalService, ToastService } from '@meplis/services';
import { ProfileAvatarComponent } from '../../../profile/profile-avatar/profile-avatar.component';
import { MenuItem } from 'apps/meplis-cnp/src/app/shared/models/menu/menu-item';
import { AppRoutes } from '@cnp/common';
import { SearchInputComponent } from '../../../search-input/search-input.component';
import { MatMenuModule } from '@angular/material/menu';
import { GroupCreateModalComponent } from '../../../../groups/group-create-modal/group-create-modal.component';
import { ProfileWidgetComponent } from '../../../profile/profile-widget/profile-widget.component';
import { ClinicalCaseCreateModalComponent } from '../../../../clinical-case/clinical-case-create-modal/clinical-case-create-modal.component';
import { ThreadCreateModalComponent } from '../../../../thread/thread-create-modal/thread-create-modal.component';
import { HubMenuComponent } from '../hub-menu/hub-menu.component';
import { HubCreateModalComponent } from '../../../../hubs/hub-create-modal/hub-create-modal.component';
import { NotificationMenuComponent } from '../../../notifications/notification-menu/notification-menu.component';
import {
  EntityInstance,
  Hub,
  SocialNetworkThreadType,
  CommonHelper,
} from '@meplis/common';
import { SocialService } from 'apps/meplis-cnp/src/app/shared/services/application/social.service';
import { CommonService } from 'apps/meplis-cnp/src/app/shared/services/application/common.service';
import { forkJoin, of, Subscription, switchMap } from 'rxjs';
import { ProfileService } from '@cnp/services';
import { AuthenticationService } from 'apps/meplis-cnp/src/app/shared/services/application/authentication.service';
import { HubService } from 'apps/meplis-cnp/src/app/shared/services/application/hub.service';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { FaIconNamePipe } from '@cnp/pipes';
import { ChatMenuComponent } from '../../../chat/chat-menu/chat-menu.component';
import { MobileBottomNavComponent } from '../../../mobile-bottom-nav/mobile-bottom-nav.component';
import { ChatFacade } from '@cnp/services/chat-facade';

@Component({
  selector: 'app-header-global',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslatePipe,
    ProfileAvatarComponent,
    ProfileWidgetComponent,
    SearchInputComponent,
    MatMenuModule,
    HubMenuComponent,
    NotificationMenuComponent,
    ChatMenuComponent,
    MobileBottomNavComponent,
   FaIconComponent, FaIconNamePipe],
  templateUrl: './header-global.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './header-global.component.scss',
})
export class HeaderGlobalComponent implements OnInit, OnDestroy {
  private _commonService = inject(CommonService);
  private _activatedRoute = inject(ActivatedRoute);
  private _router = inject(Router);
  private _modalService = inject(ModalService);
  private _toastService = inject(ToastService);
  private _socialService = inject(SocialService);
  private _profileService = inject(ProfileService);
  private _authService = inject(AuthenticationService);
  private _hubService = inject(HubService);
  private _chatFacade = inject(ChatFacade);

  @Input() public userInstance: EntityInstance;

  public logoUrl: string = 'images/meplis-logo.webp';
  public profileId: number;

  public profileBackgroundImageUrl: string = 'images/profile-background.webp';
  public mobileMenuOpened: boolean;
  public mobileSearchOpened: boolean;
  public searchFilter: string;
  public notifications: any[] = [];

  public currentRoute: string;
  public currentHub: Hub;

  public isMobile = signal(false);
  public isAuthenticated = signal(false);
  public mobileChangeSubscription: Subscription;
  public routeChangeSubscription: Subscription;
  public authSubscription: Subscription;
  public profileSubscription: Subscription;
  public userInstanceSubscription: Subscription;
  public profileChangedSubscription: Subscription;

  public notificationMenuItem: MenuItem = {
    keyTitle: 'NOTIFICATIONS.TITLE',
    iconClass: '',
    route: 'notification',
    selected: false,
  };

  public menuItems: MenuItem[] = [
    {
      keyTitle: 'FEED.TITLE',
      iconClass: 'fa-sharp fa-regular fa-comment-dots',
      route: AppRoutes['FEED'].fullPath,
      selected: false,
    },
    {
      keyTitle: 'VIDEOS.TITLE_LIST',
      iconClass: 'fa-sharp fa-regular fa-circle-play',
      route: AppRoutes['VIDEOS'].fullPath,
      selected: false,
    },
    {
      keyTitle: 'GROUP.TITLE_LIST',
      iconClass: 'fa-sharp fa-regular fa-users',
      route: AppRoutes['GROUPS'].fullPath,
      selected: false,
    },
    {
      keyTitle: 'CLINICAL_CASES.TITLE',
      iconClass: 'fa-sharp fa-regular fa-user-doctor-message',
      route: AppRoutes['CLINICAL_CASES'].fullPath,
      selected: false,
    },
    // {
    //   keyTitle: 'COURSES.LEARN_TITLE',
    //   iconClass: 'fa-sharp fa-regular fa-graduation-cap',
    //   route: AppRoutes['COURSES'].fullPath,
    //   selected: false,
    // },
    {
      keyTitle: 'EVENTS.TITLE',
      iconClass: 'fa-sharp fa-regular fa-calendar-days',
      route: AppRoutes['EVENTS'].fullPath,
      selected: false,
    },
    // {
    //   keyTitle: 'OPPORTUNITIES.TITLE',
    //   iconClass: 'fa-sharp fa-regular fa-briefcase',
    //   route: AppRoutes['OPPORTUNITIES'].fullPath,
    //   selected: false,
    // },
  ];

  /**
   * The three destinations promoted to the mobile bottom bar; everything else
   * (Videos, Events, workspaces, Create) stays one tap deeper in the drawer,
   * reached through its "More" entry. Derived from `menuItems` so labels,
   * icons and routes never drift between the two navigations.
   */
  public readonly bottomNavRouteKeys = ['FEED', 'GROUPS', 'CLINICAL_CASES'];

  public get bottomNavItems(): MenuItem[] {
    const routes = this.bottomNavRouteKeys.map(
      (key) => AppRoutes[key].fullPath
    );
    return routes
      .map((route) => this.menuItems.find((item) => item.route === route))
      .filter((item): item is MenuItem => !!item);
  }

  /** Live unread total, shared with the header dropdown's own badge. */
  public get chatUnreadCount(): number {
    return this._chatFacade.totalUnread();
  }

  constructor() {
    this._handleMobileChanges();
    this.isAuthenticated.set(this._authService.isLogged);
  }

  public ngOnInit(): void {
    this._handleInit();
    this.currentRoute = this._router.url.replace(/^\//, '');

    // this._socialService.getUserInstance().subscribe((userInstance) => {
    //   this.userInstance = userInstance;
    // });

    this.authSubscription = this._authService.onLoggedInOut
      .pipe(
        switchMap(() => {
          this.isAuthenticated.set(this._authService.isLogged);
          if (!this._authService.isLogged) {
            this.userInstance = null;
            return of(null as EntityInstance);
          }
          return this._socialService.getUserInstance();
        })
      )
      .subscribe((userInstance) => {
        this.userInstance = userInstance;
        this._handleInit();
      });

    this.profileChangedSubscription = this._profileService.onProfileChanged
      .pipe(
        switchMap(() => {
          this._socialService.clearUserInstance();
          return forkJoin({
            profile: this._profileService.getProfile(),
            userInstance: this._socialService.getUserInstance(),
          });
        })
      )
      .subscribe(({ profile, userInstance }) => {
        this.profileId = profile.id;
        this.userInstance = userInstance;
      });
  }

  ngOnDestroy(): void {
    this.mobileChangeSubscription?.unsubscribe();
    this.routeChangeSubscription?.unsubscribe();
    this.authSubscription?.unsubscribe();
    this.profileSubscription?.unsubscribe();
    this.userInstanceSubscription?.unsubscribe();
    this.profileChangedSubscription?.unsubscribe();
  }

  private _handleInit(): void {
    this.routeChangeSubscription?.unsubscribe();
    this.routeChangeSubscription = this._router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.currentRoute = event.urlAfterRedirects.replace(/^\//, '');
      }
    });

    if (!this._authService.isLogged) {
      return;
    }

    this.profileSubscription?.unsubscribe();
    this.profileSubscription = this._profileService
      .getProfile()
      .subscribe((profile) => {
        this.profileId = profile.id;
      });
  }

  public isMenuActive(item: MenuItem): boolean {
    if (!this.currentRoute || !item.route) {
      return false;
    }
    return (
      this.currentRoute === item.route ||
      this.currentRoute.startsWith(item.route + '/')
    );
  }

  public goToLogin(): void {
    const extras: NavigationExtras = {
      queryParams: {
        routeUrlToRedirect: CommonHelper.redirectableUrl(this.currentRoute),
      },
    };
    this._router.navigate([AppRoutes['LOGIN'].fullPath], extras);
  }

  public goToSignUp(): void {
    const extras: NavigationExtras = {
      queryParams: {
        routeUrlToRedirect: CommonHelper.redirectableUrl(this.currentRoute),
      },
    };
    this._router.navigate([AppRoutes['SIGN_UP'].fullPath], extras);
  }

  private _handleMobileChanges(): void {
    if (!this._commonService.isPlatformBrowser) {
      return;
    }

    const updateTabs = (isMobile: boolean) => {
      this.isMobile.set(isMobile);
    };

    updateTabs(this._commonService.isMobile);

    this.mobileChangeSubscription =
      this._commonService.onIsMobileChange.subscribe(() => {
        updateTabs(this._commonService.isMobile);
      });
  }

  public toogleSearchMobile(): void {
    this.mobileSearchOpened = !this.mobileSearchOpened;
  }

  public toogleMobileMenu(): void {
    this.mobileMenuOpened = !this.mobileMenuOpened;
  }

  /** Mobile has no dropdown -- the bottom-nav entry goes straight to /chat. */
  public onChatClick(): void {
    this.mobileMenuOpened = false;
    this._router.navigate([AppRoutes['CHAT'].fullPath]);
  }

  public selectMenu(menu: MenuItem): void {
    this.menuItems.forEach((menu) => (menu.selected = false));
    menu.selected = true;

    this.mobileMenuOpened = false;

    this._goToRoute(menu.route);
  }

  private _goToRoute(route: string): void {
    this._router.navigate([route]);
  }

  public search(): void {
    if (!this.searchFilter || this.searchFilter === '') {
      return;
    }

    this._router.navigate([AppRoutes['SEARCH'].fullPath], {
      queryParams: { q: this.searchFilter },
    });
  }

  public onSearchInputChange(query: string): void {
    if (query && query.trim() !== '') {
      this._router.navigate([AppRoutes['SEARCH'].fullPath], {
        queryParams: { q: query.trim() },
      });
    }
  }

  public onLogoClick(): void {
    const feedRoute = AppRoutes['FEED'].fullPath;
    if ('/' + this.currentRoute === feedRoute || this.currentRoute === '') {
      this._socialService.reloadThreads();
    } else {
      this._router.navigate([feedRoute]);
    }
  }

  public async openCreateThreadModal(): Promise<void> {
    const dialogRef = await this._modalService.openModal(
      ThreadCreateModalComponent,
      {
        type: SocialNetworkThreadType.POST,
        socialInstanceId: this.userInstance.token,
      }
    );

    dialogRef.afterClosed().subscribe((result: any) => {
      if (result) {
        this._router.navigate([AppRoutes['FEED'].fullPath]);
      }
    });
  }

  public async openCreateGroupModal(): Promise<void> {
    const dialogRef = await this._modalService.openModal(
      GroupCreateModalComponent
    );

    dialogRef.afterClosed().subscribe((result: any) => {
      if (result) {
        this._router.navigate([AppRoutes['GROUPS'].fullPath]);
      }
    });
  }

  public async openCreateWorkspace(): Promise<void> {
    const dialogRef = await this._modalService.openModal(
      HubCreateModalComponent,
      undefined,
      { stepped: true }
    );
    dialogRef.afterClosed().subscribe((hub: any) => {
      const token = hub?.token;
      if (token) {
        this._router.navigate([
          AppRoutes['HUB'].fullPath
            .replace(':slug', hub.slug || token)
            .replace(':id', token),
        ]);
      }
    });
  }

  public async openCreateClinicalCaseModal(): Promise<void> {
    const dialogRef = await this._modalService.openModal(
      ClinicalCaseCreateModalComponent,
      {
        socialInstanceId: this.userInstance.token,
      },
      null,
      800
    );

    dialogRef.afterClosed().subscribe((result: any) => {
      if (result) {
        this._router.navigate([AppRoutes['CLINICAL_CASES'].fullPath]);
      }
    });
  }
}
